<template>
    <v-card v-if="tariffForEdit">
        <v-card-text>
            <v-form ref="form" v-model="isValidGeneral" :disabled="isSaveLoading" data-cy="product-prices-tarrifs-form">
                <v-row dense>
                    <v-col cols="12" md="6">
                        <TextFieldLocale
                            v-model="tariffForEdit.name[selectedLocale]"
                            :label="`${$t('name')}*`"
                            :rules="[requiredRule('name')]"
                            :active-lang-code="selectedLocale"
                            hide-details="auto"
                            density="comfortable"
                        />
                    </v-col>
                    <!--
                        UI-37: disclaimer next to the Name box on the Rate-plan form,
                        top-aligned the same way as the meal-type form (UI-36). The
                        rate-plan name is guest-facing, hence the note.
                    -->
                    <v-col cols="12" md="4">
                        <div class="name-visible-disclaimer font-weight-bold">
                            <v-icon icon="mdi-information-outline" size="18" start />
                            {{ $t('product.info.name_visible_to_guests') }}
                        </div>
                    </v-col>
                    <v-col v-if="false" cols="12" md="2">
                        <v-text-field
                            v-model="tariffForEdit.contractNumber"
                            hide-details
                            :label="`${$t('contact_number')}`"
                        />
                    </v-col>
                    <v-col cols="12" md="2">
                        <div class="full-width d-flex mt-1">
                            <v-spacer />
                            <v-switch
                                v-model="tariffForEdit.active"
                                color="primary"
                                hide-details
                                :label="$t('active')"
                            />
                        </div>
                        <div v-if="tariffForEdit.id" class="full-width d-flex">
                            <v-spacer />
                            <div class="text-medium-emphasis">{{ $t('id') }}: {{ tariffForEdit.id }}</div>
                        </div>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <TextareaLocale
                            v-model="tariffForEdit.internalDescription[selectedLocale]"
                            :label="`${$t('description_internal_use')}`"
                            :rows="1"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="2">
                        <v-select
                            v-model="tariffForEdit.currencyCode"
                            :disabled="editMode"
                            :items="currencies"
                            :rules="[requiredRule('currency')]"
                            :label="`${$t('currency')}*`"
                        />
                    </v-col>
                </v-row>
                <v-row v-if="false" dense class="mt-4">
                    <v-col cols="12" md="12">
                        <TextareaLocale
                            v-model="tariffForEdit.description[selectedLocale]"
                            :label="`${$t('description')}`"
                            :rows="3"
                        />
                    </v-col>
                </v-row>
                <v-expansion-panels v-model="panel" multiple>
                    <v-expansion-panel v-if="isShowMinMaxNights">
                        <v-expansion-panel-title>
                            <span class="font-weight-bold text-subtitle-1">
                                {{ $t('tariffs_modal.flexible_check_dates') }}
                            </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <div class="d-flex align-center">
                                <span>
                                    {{ $t('tariffs_modal.flexible_check_dates_label_start') }}
                                </span>
                                <v-select
                                    v-model="tariffForEdit.flexibleDays"
                                    class="mx-2"
                                    hide-details
                                    max-width="100"
                                    :items="flexibleDayItems"
                                />
                                <span>
                                    {{ $t('tariffs_modal.flexible_check_dates_label_end') }}
                                </span>
                            </div>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-expansion-panel v-if="false">
                        <v-expansion-panel-title>
                            <span class="font-weight-bold text-subtitle-1">
                                {{ $t('price_lists') }}
                            </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <div>
                                <v-checkbox
                                    v-model="tariffForEdit.taxesIncluded"
                                    :label="$t('tariffs_modal.taxes_included')"
                                    hide-details
                                />
                            </div>
                            <v-row align="center" no-gutters class="gc-2 gr-4">
                                <v-checkbox
                                    v-model="inheritPriceListsCheckbox"
                                    :disabled="!tariffsForInheritance.length"
                                    :label="$t('tariffs_modal.inherit_price_lists')"
                                    hide-details
                                />
                                <template v-if="inheritPriceListsCheckbox">
                                    <v-select
                                        v-model="tariffForEdit.apiTariffInheritPrices.tariffId"
                                        :items="tariffsForInheritance"
                                        hide-details
                                        item-value="id"
                                        item-title="name.en"
                                        :max-width="isMobile ? null : 200"
                                        :class="{'full-width': isMobile}"
                                    />
                                    <v-select
                                        v-model="tariffForEdit.apiTariffInheritPrices.type"
                                        :items="inheritPricesTypeItems"
                                        hide-details
                                        item-value="value"
                                        :max-width="isMobile ? null : 200"
                                        :class="{'full-width': isMobile}"
                                    />
                                    <NumberInputWithButtons
                                        v-model="tariffForEdit.apiTariffInheritPrices.amount"
                                        :label="
                                            !!tariffForEdit.apiTariffInheritPrices.percentage
                                                ? $t('percent')
                                                : $t('amount')
                                        "
                                        :width="180"
                                        :buttons="[
                                            {
                                                icon: 'mdi-percent',
                                                active: !!tariffForEdit.apiTariffInheritPrices.percentage,
                                                onClick: () => (tariffForEdit.apiTariffInheritPrices.percentage = true),
                                            },
                                            {
                                                icon: 'mdi-cash',
                                                active: !tariffForEdit.apiTariffInheritPrices.percentage,
                                                onClick: () =>
                                                    (tariffForEdit.apiTariffInheritPrices.percentage = false),
                                            },
                                        ]"
                                        :append-text="
                                            !tariffForEdit.apiTariffInheritPrices.percentage
                                                ? tariffForEdit.currencyCode
                                                : null
                                        "
                                    />
                                </template>
                            </v-row>
                            <template v-if="!inheritPriceListsCheckbox">
                                <v-row>
                                    <v-col>
                                        <v-radio-group v-model="tariffForEdit.pricesApplicationType">
                                            <v-radio
                                                v-for="type in pricesApplicationTypeItems"
                                                :key="type.value"
                                                :label="type.name"
                                                :value="type.value"
                                                :selected="true"
                                            />
                                        </v-radio-group>
                                    </v-col>
                                </v-row>
                                <template
                                    v-if="
                                        tariffForEdit.pricesApplicationType === 'NETTO_PRICES' ||
                                        tariffForEdit.pricesApplicationType === 'BRUTTO_PRICES'
                                    "
                                >
                                    <div
                                        v-if="tariffForEdit.pricesApplicationType === 'BRUTTO_PRICES'"
                                        class="text-subtitle-2 font-weight-medium"
                                    >
                                        {{ $t('net_price_lists') }}
                                    </div>
                                    <v-data-table :headers="headerPriceLists" :items="tariffForEdit.priceLists">
                                        <template v-slot:item.name="{item}">
                                            <v-select
                                                v-model="item.referedToId"
                                                item-value="id"
                                                item-title="name"
                                                :items="priceLists"
                                                :label="$t('name') + '*'"
                                                hide-details
                                                :loading="isPriceListsLoading"
                                                :rules="[requiredRule('name')]"
                                            >
                                                <template v-slot:append-item>
                                                    <v-list-item @click="createPriceList">
                                                        <span class="text-primary font-weight-bold cursor-pointer">
                                                            {{ $t('create_price_list') }}
                                                        </span>
                                                    </v-list-item>
                                                </template>
                                            </v-select>
                                        </template>
                                        <template v-slot:item.active="{item}">
                                            {{ getPriceList(item.referedToId).active ? $t('active') : $t('inactive') }}
                                        </template>
                                        <template v-slot:item.dateFrom="{item}">
                                            <date-picker
                                                v-model="item.dateFrom"
                                                :max="item.dateTo || '2100-01-01'"
                                                hide-details
                                                :label="$t('date_from') + '*'"
                                                :rules="[requiredRule('date_from')]"
                                            />
                                        </template>
                                        <template v-slot:item.dateTo="{item}">
                                            <date-picker
                                                v-model="item.dateTo"
                                                outlined
                                                :label="$t('date_to') + '*'"
                                                :min="item.dateFrom || '1900-01-01'"
                                                hide-details
                                                :rules="[requiredRule('date_from')]"
                                            />
                                        </template>
                                        <template v-slot:item.minDay="{item}">
                                            <v-number-input v-model="item.minDay" hide-details :min="0" />
                                        </template>
                                        <template v-slot:item.maxDay="{item}">
                                            <v-number-input v-model="item.maxDay" hide-details :min="0" />
                                        </template>
                                        <template v-slot:item.weekDays="{item}">
                                            <days-picker v-model="item.weekDays" class="my-4" />
                                        </template>
                                        <template v-slot:item.actions="{index}">
                                            <div class="full-width d-flex align-center">
                                                <v-spacer />
                                                <v-btn
                                                    size="small"
                                                    color="error"
                                                    variant="text"
                                                    icon="mdi-close"
                                                    @click="removePriceList(index)"
                                                />
                                            </div>
                                        </template>
                                        <template v-slot:item.priority="{index}">
                                            <v-btn
                                                variant="text"
                                                icon="mdi-arrow-up-thin"
                                                @click="changePriorityInPriceList(index, 'up', 'priceLists')"
                                            />
                                            <v-btn
                                                variant="text"
                                                icon="mdi-arrow-down-thin"
                                                @click="changePriorityInPriceList(index, 'down', 'priceLists')"
                                            />
                                        </template>
                                    </v-data-table>
                                    <v-divider class="mb-4" />
                                    <v-row dense>
                                        <v-col cols="12">
                                            <div class="full-width d-flex align-center">
                                                <v-btn
                                                    variant="flat"
                                                    size="small"
                                                    color="primary"
                                                    @click="addPriceListNet"
                                                >
                                                    {{ $t('add') }}
                                                </v-btn>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </template>
                                <template v-if="tariffForEdit.pricesApplicationType === 'BRUTTO_PRICES'">
                                    <div class="text-subtitle-2 font-weight-medium mt-8">
                                        {{ $t('gross_price_lists') }}
                                    </div>
                                    <v-data-table :headers="headerPriceLists" :items="tariffForEdit.bruttoPriceLists">
                                        <template v-slot:item.name="{item}">
                                            <v-select
                                                v-model="item.referedToId"
                                                item-value="id"
                                                item-title="name"
                                                :items="priceLists"
                                                :label="$t('name') + '*'"
                                                hide-details
                                                :loading="isPriceListsLoading"
                                                :rules="[requiredRule('name')]"
                                            >
                                                <template v-slot:append-item>
                                                    <v-list-item @click="createPriceList">
                                                        <span class="text-primary font-weight-bold cursor-pointer">
                                                            {{ $t('create_price_list') }}
                                                        </span>
                                                    </v-list-item>
                                                </template>
                                            </v-select>
                                        </template>
                                        <template v-slot:item.active="{item}">
                                            {{ getPriceList(item.referedToId).active ? $t('active') : $t('inactive') }}
                                        </template>
                                        <template v-slot:item.dateFrom="{item}">
                                            <date-picker
                                                v-model="item.dateFrom"
                                                :max="item.dateTo || '2100-01-01'"
                                                hide-details
                                                :label="$t('date_from') + '*'"
                                                :rules="[requiredRule('date_from')]"
                                            />
                                        </template>
                                        <template v-slot:item.dateTo="{item}">
                                            <date-picker
                                                v-model="item.dateTo"
                                                outlined
                                                :label="$t('date_to') + '*'"
                                                :min="item.dateFrom || '1900-01-01'"
                                                hide-details
                                                :rules="[requiredRule('date_from')]"
                                            />
                                        </template>
                                        <template v-slot:item.minDay="{item}">
                                            <v-number-input v-model="item.minDay" hide-details :min="0" />
                                        </template>
                                        <template v-slot:item.maxDay="{item}">
                                            <v-number-input v-model="item.maxDay" hide-details :min="0" />
                                        </template>
                                        <template v-slot:item.weekDays="{item}">
                                            <days-picker v-model="item.weekDays" class="my-4" />
                                        </template>
                                        <template v-slot:item.actions="{index}">
                                            <div class="full-width d-flex align-center">
                                                <v-spacer />
                                                <v-btn
                                                    size="small"
                                                    color="error"
                                                    variant="text"
                                                    icon="mdi-close"
                                                    @click="removeBruttoPriceList(index)"
                                                />
                                            </div>
                                        </template>
                                        <template v-slot:item.priority="{index}">
                                            <v-btn
                                                variant="text"
                                                icon="mdi-arrow-up-thin"
                                                @click="changePriorityInPriceList(index, 'up', 'bruttoPriceLists')"
                                            />
                                            <v-btn
                                                variant="text"
                                                icon="mdi-arrow-down-thin"
                                                @click="changePriorityInPriceList(index, 'down', 'bruttoPriceLists')"
                                            />
                                        </template>
                                    </v-data-table>
                                    <v-divider class="mb-4" />
                                    <v-row dense>
                                        <v-col cols="12">
                                            <div class="full-width d-flex align-center">
                                                <v-btn
                                                    variant="flat"
                                                    size="small"
                                                    color="primary"
                                                    @click="addPriceListGross"
                                                >
                                                    {{ $t('add') }}
                                                </v-btn>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </template>
                            </template>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-expansion-panel v-if="false">
                        <v-expansion-panel-title>
                            <span class="font-weight-bold text-subtitle-1">
                                {{ $t('price_lists_extra_services') }}
                            </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-data-table
                                :headers="headerPriceListsExtraServices"
                                :items="tariffForEdit.extraServicesPriceLists"
                            >
                                <template v-slot:item.name="{item}">
                                    <v-select
                                        v-model="item.extraServicesPriceListId"
                                        item-value="id"
                                        item-title="name"
                                        class="my-4"
                                        :items="priceListsExtras"
                                        :label="$t('name') + '*'"
                                        hide-details
                                        :loading="isPriceListsExtrasLoading"
                                        :rules="[requiredRule('name')]"
                                    >
                                        <template v-slot:append-item>
                                            <v-list-item @click="createPriceListExtra">
                                                <span class="text-primary font-weight-bold cursor-pointer">
                                                    {{ $t('create_price_list_extra') }}
                                                </span>
                                            </v-list-item>
                                        </template>
                                    </v-select>
                                </template>
                                <template v-slot:item.dateFrom="{item}">
                                    <date-picker
                                        v-model="item.dateFrom"
                                        :max="item.dateTo || '2100-01-01'"
                                        hide-details
                                        :label="$t('date_from') + '*'"
                                        :rules="[requiredRule('date_from')]"
                                    />
                                </template>
                                <template v-slot:item.dateTo="{item}">
                                    <date-picker
                                        v-model="item.dateTo"
                                        outlined
                                        :label="$t('date_to') + '*'"
                                        :min="item.dateFrom || '1900-01-01'"
                                        hide-details
                                        :rules="[requiredRule('date_from')]"
                                    />
                                </template>
                                <template v-slot:item.minDay="{item}">
                                    <v-number-input v-model="item.minDay" hide-details :min="0" />
                                </template>
                                <template v-slot:item.maxDay="{item}">
                                    <v-number-input v-model="item.maxDay" hide-details :min="0" />
                                </template>
                                <template v-slot:item.actions="{index}">
                                    <div class="full-width d-flex align-center">
                                        <v-spacer />
                                        <v-btn
                                            size="small"
                                            color="error"
                                            variant="text"
                                            icon="mdi-close"
                                            @click="removePriceListExtra(index)"
                                        />
                                    </div>
                                </template>
                            </v-data-table>
                            <v-divider class="mb-4" />
                            <v-row dense>
                                <v-col cols="12">
                                    <div class="full-width d-flex align-center">
                                        <v-btn variant="flat" size="small" color="primary" @click="addPriceListExtra">
                                            {{ $t('add') }}
                                        </v-btn>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-expansion-panel v-if="false">
                        <v-expansion-panel-title>
                            <span class="font-weight-bold text-subtitle-1">
                                {{ $t('commission') }}
                            </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-data-table :headers="headerCommissions" :items="tariffForEdit.commissions">
                                <template v-slot:item.name="{item}">
                                    <v-select
                                        v-model="item.commissionId"
                                        item-value="id"
                                        item-title="planName"
                                        class="my-4"
                                        :items="commissions"
                                        :label="$t('name') + '*'"
                                        hide-details
                                        :loading="isCommissionsLoading"
                                        :rules="[requiredRule('name')]"
                                    >
                                        <template v-slot:append-item>
                                            <v-list-item @click="createCommission">
                                                <span class="text-primary font-weight-bold cursor-pointer">
                                                    {{ $t('commission_creating') }}
                                                </span>
                                            </v-list-item>
                                        </template>
                                    </v-select>
                                </template>
                                <template v-slot:item.active="{item}">
                                    {{ getCommission(item.commissionId).active ? $t('active') : $t('inactive') }}
                                </template>
                                <template v-slot:item.dateFrom="{item}">
                                    <date-picker
                                        v-model="item.dateFrom"
                                        :max="item.dateTo || '2100-01-01'"
                                        hide-details
                                        :label="$t('date_from') + '*'"
                                        :rules="[requiredRule('date_from')]"
                                    />
                                </template>
                                <template v-slot:item.dateTo="{item}">
                                    <date-picker
                                        v-model="item.dateTo"
                                        outlined
                                        :label="$t('date_to') + '*'"
                                        :min="item.dateFrom || '1900-01-01'"
                                        hide-details
                                        :rules="[requiredRule('date_from')]"
                                    />
                                </template>
                                <template v-slot:item.actions="{index}">
                                    <div class="full-width d-flex align-center">
                                        <v-spacer />
                                        <v-btn
                                            size="small"
                                            color="error"
                                            variant="text"
                                            icon="mdi-close"
                                            @click="removeCommission(index)"
                                        />
                                    </div>
                                </template>
                                <template v-slot:item.priority="{index}">
                                    <v-btn
                                        variant="text"
                                        icon="mdi-arrow-up-thin"
                                        @click="changePriorityInPriceList(index, 'up', 'commissions')"
                                    />
                                    <v-btn
                                        variant="text"
                                        icon="mdi-arrow-down-thin"
                                        @click="changePriorityInPriceList(index, 'down', 'commissions')"
                                    />
                                </template>
                            </v-data-table>
                            <v-divider class="mb-4" />
                            <v-row dense>
                                <v-col cols="12">
                                    <div class="full-width d-flex align-center">
                                        <v-btn variant="flat" size="small" color="primary" @click="addCommission">
                                            {{ $t('add') }}
                                        </v-btn>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-expansion-panel>
                        <v-expansion-panel-title>
                            <span class="font-weight-bold text-subtitle-1">
                                {{ $t('discounts') }}
                            </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-data-table :headers="headerDiscounts" :items="tariffForEdit.discountsRule.discounts">
                                <template v-slot:item.name="{item}">
                                    <v-select
                                        v-model="item.referedToId"
                                        item-value="id"
                                        :item-title="getTitleKeyByDiscountReferedToId(item.referedToId)"
                                        class="my-4"
                                        :items="activeDiscounts"
                                        :label="$t('name') + '*'"
                                        hide-details
                                        :loading="isDiscountsLoading"
                                        :rules="[requiredRule('name')]"
                                    >
                                        <template v-slot:append-item>
                                            <v-list-item @click="createDiscount">
                                                <span class="text-primary font-weight-bold cursor-pointer">
                                                    {{ $t('discount_creating') }}
                                                </span>
                                            </v-list-item>
                                        </template>
                                    </v-select>
                                </template>
                                <template v-slot:item.dateFrom="{item}">
                                    <date-picker
                                        v-model="item.dateFrom"
                                        :max="item.dateTo || '2100-01-01'"
                                        hide-details
                                        :label="$t('date_from') + '*'"
                                        :rules="[requiredRule('date_from')]"
                                    />
                                </template>
                                <template v-slot:item.dateTo="{item}">
                                    <date-picker
                                        v-model="item.dateTo"
                                        outlined
                                        :label="$t('date_to') + '*'"
                                        :min="item.dateFrom || '1900-01-01'"
                                        hide-details
                                        :rules="[requiredRule('date_from')]"
                                    />
                                </template>
                                <template v-slot:item.actions="{index}">
                                    <div class="full-width d-flex align-center">
                                        <v-spacer />
                                        <v-btn
                                            size="small"
                                            color="error"
                                            variant="text"
                                            icon="mdi-close"
                                            @click="removeDiscount(index)"
                                        />
                                    </div>
                                </template>
                                <template v-slot:item.priority="{index}">
                                    <v-btn
                                        variant="text"
                                        icon="mdi-arrow-up-thin"
                                        @click="changePriorityInPriceList(index, 'up', 'discountsRule.discounts')"
                                    />
                                    <v-btn
                                        variant="text"
                                        icon="mdi-arrow-down-thin"
                                        @click="changePriorityInPriceList(index, 'down', 'discountsRule.discounts')"
                                    />
                                </template>
                                <template v-slot:item.discountApplicability="{item}">
                                    <v-select
                                        v-model="item.discountApplicability"
                                        :items="discountApplicabilityItems"
                                        :item-value="'value'"
                                        hide-details
                                        :item-title="'title'"
                                    />
                                </template>
                                <template v-slot:item.discountCategory="{item}">
                                    <v-select
                                        v-model="item.discountCategory"
                                        :items="discountCategoryItems"
                                        :item-value="'value'"
                                        hide-details
                                        :item-title="'title'"
                                    />
                                </template>
                            </v-data-table>
                            <v-divider class="mb-4" />
                            <v-row dense>
                                <v-col cols="12">
                                    <div class="full-width d-flex align-center">
                                        <v-btn variant="flat" size="small" color="primary" @click="addDiscount">
                                            {{ $t('add') }}
                                        </v-btn>
                                    </div>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <v-radio-group
                                        v-model="tariffForEdit.discountsRule.discountsApplying"
                                        :label="$t('discounts_applying_type_radio.title')"
                                    >
                                        <v-radio
                                            v-for="type in discountsApplyingTypesItems"
                                            :key="type.value"
                                            :label="type.title"
                                            :value="type.value"
                                        />
                                    </v-radio-group>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-expansion-panel v-if="isShowMealTypes">
                        <v-expansion-panel-title>
                            <span class="font-weight-bold text-subtitle-1">
                                {{ $t('product_tabs.meal_types') }}
                            </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <TariffMealTypes v-model="tariffForEdit.secondaryServices" />
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-expansion-panel v-if="false">
                        <v-expansion-panel-title>
                            <span class="font-weight-bold text-subtitle-1">
                                {{ $t('terms_of_use') }}
                            </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-data-table :headers="headerUseTerms" :items="tariffForEdit.useTerms">
                                <template v-slot:item.daysPrior="{item}">
                                    <div class="d-flex flex-column my-4">
                                        <v-number-input v-model="item.daysPrior" hide-details />
                                    </div>
                                </template>
                                <template v-slot:item.bookingDateFrom="{item}">
                                    <date-picker
                                        v-model="item.bookingDateFrom"
                                        :max="item.bookingDateTo || '2100-01-01'"
                                        hide-details
                                        :label="$t('date_from') + '*'"
                                        :rules="[requiredRule('date_from')]"
                                    />
                                </template>
                                <template v-slot:item.bookingDateTo="{item}">
                                    <date-picker
                                        v-model="item.bookingDateTo"
                                        outlined
                                        :label="$t('date_to') + '*'"
                                        :min="item.bookingDateFrom || '1900-01-01'"
                                        hide-details
                                        :rules="[requiredRule('date_to')]"
                                    />
                                </template>
                                <template v-slot:item.checkInDateFrom="{item}">
                                    <date-picker
                                        v-model="item.checkInDateFrom"
                                        :max="item.checkInDateTo || '2100-01-01'"
                                        hide-details
                                        :label="$t('date_from') + '*'"
                                        :rules="[requiredRule('date_from')]"
                                    />
                                </template>
                                <template v-slot:item.checkInDateTo="{item}">
                                    <date-picker
                                        v-model="item.checkInDateTo"
                                        outlined
                                        :label="$t('date_to') + '*'"
                                        :min="item.checkInDateFrom || '1900-01-01'"
                                        hide-details
                                        :rules="[requiredRule('date_to')]"
                                    />
                                </template>
                                <template v-slot:item.actions="{index}">
                                    <div class="full-width d-flex align-center">
                                        <v-spacer />
                                        <v-btn
                                            size="small"
                                            color="error"
                                            variant="text"
                                            icon="mdi-close"
                                            @click="removeUseTerm(index)"
                                        />
                                    </div>
                                </template>
                            </v-data-table>
                            <v-divider class="mb-4" />
                            <v-row dense>
                                <v-col cols="12">
                                    <div class="full-width d-flex align-center">
                                        <v-btn variant="flat" size="small" color="primary" @click="addUseTerm">
                                            {{ $t('add') }}
                                        </v-btn>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-expansion-panel>
                        <v-expansion-panel-title>
                            <span class="font-weight-bold text-subtitle-1">
                                {{ $t('penalties') }}
                            </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-data-table :headers="headerPenalties" :items="tariffForEdit.penalties">
                                <template v-slot:item.name="{item}">
                                    <v-select
                                        v-model="item.penaltyId"
                                        item-value="id"
                                        item-title="displayName"
                                        class="my-4"
                                        :items="penaltiesWithStatus"
                                        :label="$t('name') + '*'"
                                        hide-details
                                        :loading="isPenaltiesLoading"
                                        :rules="[requiredRule('name')]"
                                    >
                                        <template v-slot:append-item>
                                            <v-list-item @click="createPenalty">
                                                <span class="text-primary font-weight-bold cursor-pointer">
                                                    {{ $t('penalty_creating') }}
                                                </span>
                                            </v-list-item>
                                        </template>
                                    </v-select>
                                </template>
                                <template v-slot:item.dateFrom="{item}">
                                    <date-picker
                                        v-model="item.dateFrom"
                                        :max="item.dateTo || '2100-01-01'"
                                        hide-details
                                        :label="$t('date_from') + '*'"
                                        :rules="[requiredRule('date_from')]"
                                    />
                                </template>
                                <template v-slot:item.dateTo="{item}">
                                    <date-picker
                                        v-model="item.dateTo"
                                        outlined
                                        :label="$t('date_to') + '*'"
                                        :min="item.dateFrom || '1900-01-01'"
                                        hide-details
                                        :rules="[requiredRule('date_from')]"
                                    />
                                </template>
                                <template v-slot:item.actions="{index}">
                                    <div class="full-width d-flex align-center">
                                        <v-spacer />
                                        <v-btn
                                            size="small"
                                            color="error"
                                            variant="text"
                                            icon="mdi-close"
                                            @click="removePenalty(index)"
                                        />
                                    </div>
                                </template>
                                <template v-slot:item.priority="{index}">
                                    <v-btn
                                        variant="text"
                                        icon="mdi-arrow-up-thin"
                                        @click="changePriorityInPriceList(index, 'up', 'penalties')"
                                    />
                                    <v-btn
                                        variant="text"
                                        icon="mdi-arrow-down-thin"
                                        @click="changePriorityInPriceList(index, 'down', 'penalties')"
                                    />
                                </template>
                            </v-data-table>
                            <v-divider class="mb-4" />
                            <v-row dense>
                                <v-col cols="12">
                                    <div class="full-width d-flex align-center">
                                        <v-btn variant="flat" size="small" color="primary" @click="addPenalty">
                                            {{ $t('add') }}
                                        </v-btn>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-expansion-panel v-if="false">
                        <v-expansion-panel-title>
                            <span class="font-weight-bold text-subtitle-1">
                                {{ $t('membership_levels') }}
                            </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-checkbox v-model="isAllMembershipsSelected" hide-details>
                                        <template v-slot:label>
                                            <span class="text-capitalize">
                                                {{ $t('all') }}
                                            </span>
                                        </template>
                                    </v-checkbox>
                                    <v-divider />
                                    <v-checkbox
                                        v-for="membership in membershipLevelsItems"
                                        :key="membership"
                                        v-model="tariffForEdit.membershipLevels"
                                        hide-details
                                        :label="$t(`discount_membership.${membership}`)"
                                        :value="membership"
                                    />
                                </v-col>
                            </v-row>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-expansion-panel v-if="false">
                        <v-expansion-panel-title>
                            <span class="font-weight-bold text-subtitle-1">
                                {{ $t('citizenship_levels') }}
                            </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-checkbox v-model="isAllCitizenshipsSelected" hide-details>
                                        <template v-slot:label>
                                            <span class="text-capitalize">
                                                {{ $t('all') }}
                                            </span>
                                        </template>
                                    </v-checkbox>
                                    <v-divider />
                                    <v-virtual-scroll :height="300" :items="countries">
                                        <template v-slot="{item}">
                                            <v-checkbox
                                                v-model="tariffForEdit.limitsOnCitizenship"
                                                hide-details
                                                :label="item.name"
                                                :value="item.id"
                                            />
                                        </template>
                                    </v-virtual-scroll>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-expansion-panel v-if="false">
                        <v-expansion-panel-title>
                            <span class="font-weight-bold text-subtitle-1">
                                {{ $t('suballotments') }}
                            </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-data-table
                                :items="tariffForEdit.apiTariffSubAllotments"
                                :headers="headersSubAllotments"
                                :hide-default-header="isMobile"
                            >
                                <template v-slot:item.name="{item}">
                                    <div class="my-3">
                                        <v-select
                                            v-model="item.id"
                                            max-width="350"
                                            :loading="isSubAllotmentsLoading"
                                            :items="getSuballotmentItems(item)"
                                            item-value="id"
                                            item-title="name"
                                            hide-details
                                            :density="'compact'"
                                        />
                                    </div>
                                </template>
                                <template v-slot:item.actions="{index}">
                                    <div class="full-width d-flex align-center">
                                        <v-spacer />
                                        <v-btn
                                            size="small"
                                            color="error"
                                            variant="text"
                                            icon="mdi-close"
                                            @click="removeSubAllotment(index)"
                                        />
                                    </div>
                                </template>
                                <template v-slot:item.mobile="{item, index}">
                                    <v-col cols="12" class="mt-4 px-0">
                                        <v-select
                                            v-model="item.id"
                                            :items="subAllotments"
                                            item-value="id"
                                            item-title="name"
                                            hide-details
                                            :density="'compact'"
                                        />
                                    </v-col>
                                    <v-col cols="12" class="px-0">
                                        <div class="full-width d-flex align-center">
                                            <v-spacer />
                                            <v-btn
                                                size="small"
                                                color="error"
                                                variant="text"
                                                icon="mdi-close"
                                                @click="removeSubAllotment(index)"
                                            />
                                        </div>
                                    </v-col>
                                </template>
                            </v-data-table>
                            <v-divider class="mb-4" />
                            <v-row dense>
                                <v-col cols="12">
                                    <div class="full-width d-flex align-center">
                                        <v-btn
                                            v-if="getSuballotmentItems().length"
                                            variant="flat"
                                            size="small"
                                            color="primary"
                                            @click="addSubAllotment"
                                        >
                                            {{ $t('add') }}
                                        </v-btn>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-expansion-panel v-if="false">
                        <v-expansion-panel-title>
                            <span class="font-weight-bold text-subtitle-1">
                                {{ $t('tags') }}
                            </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-text-field v-model="tags" :label="$t('tags')" />
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-expansion-panel v-if="false">
                        <v-expansion-panel-title>
                            <span class="font-weight-bold text-subtitle-1">
                                {{ $t('reservation_modification') }}
                            </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-checkbox
                                        v-for="item in orderModificationItems"
                                        :key="item"
                                        v-model="tariffForEdit.orderModification[item]"
                                        hide-details
                                        :label="$t(`tariff_reservation_modification.${item}`)"
                                    />
                                </v-col>
                            </v-row>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-form>
        </v-card-text>
        <v-card-actions class="d-flex justify-start px-6 pb-6 pt-4">
            <v-btn
                class="save-btn"
                variant="elevated"
                color="primary"
                :loading="isSaveLoading"
                data-cy="save"
                @click="save"
            >
                {{ $t('save') }}
            </v-btn>
            <v-btn class="cancel-btn" variant="text" data-cy="close" @click="onClose">
                {{ $t('cancel') }}
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, Provide} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import DaterangeM from '~src/components/search/forms/daterangeM.src'
    import {clone} from '~/utils/helpers'
    import DaysPicker from '~/components/supplier/DaysPicker.vue'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'
    import {contractServiceType} from '~/utils/types/types'

    @Component({
        components: {
            TextFieldLocale,
            DaysPicker,
            DaterangeM,
            DatePicker,
        },
    })
    export class ProductPricesTariffsModal extends mixins(FormValidationRulesMixin) {
        @Prop() tariff
        @Prop() contract
        @Prop() selectedLocale
        @Prop() priceLists
        @Prop() tariffs
        @Prop() isPriceListsLoading
        @Prop() priceListsExtras
        @Prop() isPriceListsExtrasLoading
        @Prop() commissions
        @Prop() isCommissionsLoading
        @Prop() penalties
        @Prop() isPenaltiesLoading
        @Prop() discounts
        @Prop() isDiscountsLoading
        @Provide get activeLangCode() {
            return this.selectedLocale
        }

        isValidGeneral = false
        tariffForEdit = null
        isSaveLoading = false
        editMode = false

        isSubAllotmentsLoading = false
        subAllotments = []

        panel = []

        async mounted() {
            await this.initValues()
            await this.loadSubAllotments()
        }

        async loadSubAllotments() {
            try {
                this.isSubAllotmentsLoading = true
                this.subAllotments = await this.$api.subAllotments.get({
                    contractId: this.contract?.id || $route.params?.contractId,
                    active: true,
                })
            } catch {
                this.subAllotments = []
            } finally {
                this.isSubAllotmentsLoading = false
            }
        }

        async initValues() {
            if (this.tariff) {
                this.tariffForEdit = clone(this.tariff)
                if (!this.tariffForEdit?.discountsRule) {
                    this.tariffForEdit.discountsRule = {
                        discounts: [],
                        discountsApplying: 'ALL',
                    }
                }

                this.tariffForEdit.apiTariffSubAllotments = this.tariffForEdit.apiTariffSubAllotments.map(el => ({
                    id: el.id,
                }))
                this.tariffForEdit.priceLists.sort((a, b) => a.priority - b.priority)
            } else {
                this.tariffForEdit = {
                    contractId: this.contract.id,
                    name: {},
                    internalDescription: {},
                    description: {},
                    active: true,
                    contractNumber: '',
                    hideName: false,
                    currencyCode: supplierStore.fullContract.transactionsCurrency || this.currencies[0].value,
                    apiTariffSubAllotments: [],
                    flexibleDays: 0,
                    ...(this.allowTags && {tags: ['public']}),
                    priceLists: [],
                    bruttoPriceLists: [],
                    taxesIncluded: false,
                    pricesApplicationType: this.pricesApplicationTypeItems[0].value,
                    commissions: [],
                    penalties: [],
                    membershipLevels: [],
                    discountsRule: {
                        discounts: [],
                        discountsApplying: 'ALL',
                    },
                    useTerms: [],
                    limitsOnCitizenship: [],
                    orderModification: {
                        orderChangeDates: true,
                        orderChangeAllTourists: true,
                        orderChangeExtraServices: true,
                        ...(this.contract.serviceType === contractServiceType.ownHotel && {
                            orderChangeTourLeader: false,
                        }),
                    },
                }
            }

            this.editMode = !!this.tariffForEdit.id
        }

        get tags() {
            return this.tariffForEdit.tags.join(',')
        }

        set tags(val) {
            this.tariffForEdit.tags = val
                .split(',')
                .map(el => el.trim())
                .filter(el => el.length)
        }

        clear() {
            this.tariffForEdit = null
        }

        @Emit()
        onClose() {
            this.clear()
        }

        @Emit()
        createPriceList() {}

        @Emit()
        createPriceListExtra() {}

        @Emit()
        createCommission() {}

        @Emit()
        createPenalty() {}

        @Emit()
        createDiscount() {}

        getPriceList(id) {
            return this.priceLists.find(el => el.id === id)
        }

        getCommission(id) {
            return this.commissions.find(el => el.id === id)
        }

        async save() {
            await this.$refs.form.validate()
            if (!this.isValidGeneral) return

            if (this.editMode) {
                await this.saveEdit()
            } else {
                await this.saveCreate()
            }
        }

        @Emit()
        onSave() {}

        async saveEdit() {
            try {
                this.isSaveLoading = true

                await this.$api.ownProductTariffs.put(this.tariffForEdit)

                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.onClose()
                this.onSave()
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.isSaveLoading = false
            }
        }

        async saveCreate() {
            try {
                this.isSaveLoading = true

                await this.$api.ownProductTariffs.post(this.tariffForEdit)

                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.onClose()
                this.onSave()
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.isSaveLoading = false
            }
        }

        removeSubAllotment(index) {
            this.tariffForEdit.apiTariffSubAllotments.splice(index, 1)
        }

        removePriceList(index) {
            this.tariffForEdit.priceLists.splice(index, 1)
        }

        removeBruttoPriceList(index) {
            this.tariffForEdit.bruttoPriceLists.splice(index, 1)
        }

        removePriceListExtra(index) {
            this.tariffForEdit.extraServicesPriceLists.splice(index, 1)
        }

        removeCommission(index) {
            this.tariffForEdit.commissions.splice(index, 1)
        }

        removePenalty(index) {
            this.tariffForEdit.penalties.splice(index, 1)
        }

        removeDiscount(index) {
            this.tariffForEdit.discountsRule.discounts.splice(index, 1)
        }

        removeUseTerm(index) {
            this.tariffForEdit.useTerms.splice(index, 1)
        }

        addSubAllotment() {
            const items = this.getSuballotmentItems()

            this.tariffForEdit.apiTariffSubAllotments.push({id: items[0].id})
        }

        addPriceListNet() {
            this.tariffForEdit.priceLists.push({
                ...(this.priceLists?.length && {referedToId: this.priceLists[0]?.id}),
                weekDays: [true, true, true, true, true, true, true],
                dateFrom: null,
                dateTo: null,
                ...(this.isShowMinMaxNights && {
                    minDay: null,
                    maxDay: null,
                }),
                priority: this.tariffForEdit.priceLists?.length ? this.tariffForEdit.priceLists.length + 1 : 1,
            })
        }

        addPriceListGross() {
            this.tariffForEdit.bruttoPriceLists.push({
                ...(this.priceLists?.length && {referedToId: this.priceLists[0]?.id}),
                weekDays: [true, true, true, true, true, true, true],
                dateFrom: null,
                dateTo: null,
                ...(this.isShowMinMaxNights && {
                    minDay: null,
                    maxDay: null,
                }),
                priority: this.tariffForEdit.bruttoPriceLists?.length
                    ? this.tariffForEdit.bruttoPriceLists.length + 1
                    : 1,
            })
        }

        addPriceListExtra() {
            this.tariffForEdit.extraServicesPriceLists.push({
                ...(this.priceListsExtras?.length && {extraServicesPriceListId: this.priceListsExtras[0]?.id}),
                dateFrom: null,
                dateTo: null,
                ...(this.isShowMinMaxNights && {
                    minDay: null,
                    maxDay: null,
                }),
                priority: this.tariffForEdit.extraServicesPriceLists?.length
                    ? this.tariffForEdit.extraServicesPriceLists.length + 1
                    : 1,
            })
        }

        addCommission() {
            this.tariffForEdit.commissions.push({
                ...(this.commissions?.length && {commissionId: this.commissions[0]?.id}),
                dateFrom: null,
                dateTo: null,
                priority: this.tariffForEdit.commissions?.length ? this.tariffForEdit.commissions.length + 1 : 1,
            })
        }

        addPenalty() {
            this.tariffForEdit.penalties.push({
                ...(this.penalties?.length && {penaltyId: this.penalties[0]?.id}),
                dateFrom: null,
                dateTo: null,
                priority: this.tariffForEdit.penalties?.length ? this.tariffForEdit.penalties.length + 1 : 1,
            })
        }

        addDiscount() {
            this.tariffForEdit.discountsRule.discounts.push({
                ...(this.discounts?.length && {referedToId: this.discounts[0]?.id}),
                discountCategory: this.discountCategoryItems[0].value,
                dateFrom: null,
                dateTo: null,
                priority: this.tariffForEdit.discountsRule.discounts?.length
                    ? this.tariffForEdit.discountsRule.discounts.length + 1
                    : 1,
            })
        }

        addUseTerm() {
            this.tariffForEdit.useTerms.push({
                daysPrior: null,
                bookingDateFrom: null,
                bookingDateTo: null,
                checkInDateFrom: null,
                checkInDateTo: null,
            })
        }

        getSuballotmentItems(item) {
            const filteredSubAllotments = this.subAllotments.filter(
                el => !this.tariffForEdit.apiTariffSubAllotments.find(ts => ts.id === el.id)
            )

            if (!item) {
                return [...filteredSubAllotments]
            }

            const currentSubAllotment = this.subAllotments.find(el => el.id === item.id)

            return currentSubAllotment ? [currentSubAllotment, ...filteredSubAllotments] : [...filteredSubAllotments]
        }

        changePriorityInPriceList(index, direction, listName) {
            const item = this.tariffForEdit[listName][index]
            if (direction === 'up' && item.priority > 1) {
                item.priority--
            } else if (direction === 'down' && item.priority < this.tariffForEdit[listName].length) {
                item.priority++
            }

            this.tariffForEdit[listName] = this.tariffForEdit[listName]
                .map((el, i) => {
                    if (i !== index) {
                        if (direction === 'up' && el.priority === item.priority) {
                            el.priority++
                        } else if (direction === 'down' && el.priority === item.priority) {
                            el.priority--
                        }
                    }
                    return el
                })
                .sort((a, b) => a.priority - b.priority)
        }

        get tariffsForInheritance() {
            return this.tariffs
                .filter(el => el.active)
                .filter(el => el.id !== this.tariffForEdit.id)
                .filter(el => !el.inheritanceEnabled || el.id === this.tariffForEdit?.apiTariffInheritPrices?.tariffId)
        }

        getTitleKeyByDiscountReferedToId(referedToId) {
            const discount = this.discounts.find(discount => discount.id === referedToId)
            return discount.name?.[this.selectedLocale]
                ? `name.${this.selectedLocale}`
                : `name.${Object.keys(discount.name)[0]}`
        }

        get allowTags() {
            return runtimeStore.getCRMOption('asp.option.allow.tags')
        }

        get inheritPricesTypeItems() {
            return ['DISCOUNT', 'MARKUP'].map(el => ({
                title: this.$t(`inherit_prices_type.${el}`),
                value: el,
            }))
        }

        get inheritPriceListsCheckbox() {
            return this.tariffForEdit.inheritanceEnabled
        }

        set inheritPriceListsCheckbox(val) {
            this.tariffForEdit.priceLists = []

            if (val) {
                this.tariffForEdit.apiTariffInheritPrices = {
                    tariffId: this.tariffsForInheritance?.[0]?.id,
                    type: this.inheritPricesTypeItems[0].value,
                    percentage: true,
                    amount: 0,
                }
                this.tariffForEdit.inheritanceEnabled = true
            } else {
                if (this.tariffForEdit.apiTariffInheritPrices) {
                    this.tariffForEdit.apiTariffInheritPrices.delete = true
                }

                this.tariffForEdit.inheritanceEnabled = false
            }
        }

        get currencies() {
            return Object.values(this.$conf.public.currency.available)
                .filter(currency => Object.keys(runtimeStore.exchangeRates).includes(currency))
                .map(currency => ({
                    title: currency,
                    value: currency,
                }))
        }

        get headersSubAllotments() {
            if (this.isMobile) {
                return [{title: '', value: 'mobile'}]
            }
            return [
                {title: this.$t('name'), value: 'name'},
                {title: this.$t('actions'), value: 'actions', align: 'end'},
            ]
        }

        get headerPriceLists() {
            return [
                {title: this.$t('name'), value: 'name'},
                {title: this.$t('active'), value: 'active'},
                {title: this.$t('date_from'), value: 'dateFrom'},
                {title: this.$t('date_to'), value: 'dateTo'},
                ...(this.isShowMinMaxNights ? [{title: this.$t('number_of_nights_min'), value: 'minDay'}] : []),
                ...(this.isShowMinMaxNights ? [{title: this.$t('number_of_nights_max'), value: 'maxDay'}] : []),
                {title: this.$t('weekdays'), value: 'weekDays'},
                {title: this.$t('priority'), value: 'priority', align: 'center'},
                {title: this.$t('actions'), value: 'actions', align: 'end'},
            ]
        }

        get headerPriceListsExtraServices() {
            return [
                {title: this.$t('name'), value: 'name'},
                {title: this.$t('date_from'), value: 'dateFrom'},
                {title: this.$t('date_to'), value: 'dateTo'},
                ...(this.isShowMinMaxNights ? [{title: this.$t('number_of_nights_min'), value: 'minDay'}] : []),
                ...(this.isShowMinMaxNights ? [{title: this.$t('number_of_nights_max'), value: 'maxDay'}] : []),
                {title: this.$t('actions'), value: 'actions', align: 'end'},
            ]
        }

        get headerCommissions() {
            return [
                {title: this.$t('name'), value: 'name'},
                {title: this.$t('active'), value: 'active'},
                {title: this.$t('date_from'), value: 'dateFrom'},
                {title: this.$t('date_to'), value: 'dateTo'},
                {title: this.$t('priority'), value: 'priority', align: 'center'},
                {title: this.$t('actions'), value: 'actions', align: 'end'},
            ]
        }

        get headerPenalties() {
            return [
                {title: this.$t('name'), value: 'name'},
                {title: this.$t('date_from'), value: 'dateFrom'},
                {title: this.$t('date_to'), value: 'dateTo'},
                {title: this.$t('priority'), value: 'priority', align: 'center'},
                {title: this.$t('actions'), value: 'actions', align: 'end'},
            ]
        }

        get penaltiesWithStatus() {
            if (!this.penalties || !Array.isArray(this.penalties)) {
                return []
            }
            return this.penalties.map(penalty => ({
                ...penalty,
                displayName: penalty.active ? penalty.name : `${penalty.name} (${this.$t('inactive')})`,
            }))
        }

        get headerDiscounts() {
            return [
                {title: this.$t('name'), value: 'name'},
                {title: this.$t('apply_discount'), value: 'discountApplicability'},
                {title: this.$t('discount_type'), value: 'discountCategory'},
                {title: this.$t('date_from'), value: 'dateFrom'},
                {title: this.$t('date_to'), value: 'dateTo'},
                {title: this.$t('priority'), value: 'priority', align: 'center'},
                {title: this.$t('actions'), value: 'actions', align: 'end'},
            ]
        }

        get headerUseTerms() {
            return [
                {title: this.$t('days_prior'), value: 'daysPrior'},
                {title: this.$t('booking_date_from'), value: 'bookingDateFrom'},
                {title: this.$t('booking_date_to'), value: 'bookingDateTo'},
                {title: this.$t('check_in_date_from'), value: 'checkInDateFrom'},
                {title: this.$t('check_in_date_to'), value: 'checkInDateFrom'},
                {title: this.$t('actions'), value: 'actions', align: 'end'},
            ]
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get flexibleDayItems() {
            return Array.from(Array(15).keys())
        }

        get pricesApplicationTypeItems() {
            return [...(this.contract.serviceType === contractServiceType.ownHotel ? ['DAILY_RATES'] : [])].map(el => ({
                value: el,
                name: this.$t(`price_list_application_type.${el}`),
            }))
        }

        get membershipLevelsItems() {
            return ['GUEST', 'REGISTERED', 'REGISTRATION_PENDING']
        }

        get orderModificationItems() {
            return [
                'orderChangeAllTourists',
                'orderChangeDates',
                'orderChangeExtraServices',
                ...(this.contract.serviceType === contractServiceType.ownHotel ? ['orderChangeTourLeader'] : []),
            ]
        }

        get isAllMembershipsSelected() {
            return this.membershipLevelsItems.every(ml => this.tariffForEdit.membershipLevels.includes(ml))
        }

        set isAllMembershipsSelected(val) {
            if (val === true) {
                this.tariffForEdit.membershipLevels = [...this.membershipLevelsItems]
            } else {
                this.tariffForEdit.membershipLevels = []
            }
        }

        get discountCategoryItems() {
            return ['SUPPLIER', 'TO'].map(el => ({
                title: this.$t(`discountCategory.${el}`),
                value: el,
            }))
        }

        get discountApplicabilityItems() {
            return ['OPTIONAL', 'ALWAYS'].map(el => ({
                title: this.$t(`discountApplicability.${el}`),
                value: el,
            }))
        }

        get countries() {
            return runtimeStore.countries
        }

        get isAllCitizenshipsSelected() {
            return this.countries.map(el => el.id).every(ml => this.tariffForEdit.limitsOnCitizenship.includes(ml))
        }

        set isAllCitizenshipsSelected(val) {
            if (val === true) {
                this.tariffForEdit.limitsOnCitizenship = this.countries.map(el => el.id)
            } else {
                this.tariffForEdit.limitsOnCitizenship = []
            }
        }

        get discountsApplyingTypesItems() {
            return ['ALL', 'FIRST', 'BEST'].map(el => ({
                title: this.$t(`discounts_applying_type_radio.${el}`),
                value: el,
            }))
        }

        get isShowMealTypes() {
            const availableServices = [
                contractServiceType.ownHotel,
                contractServiceType.ownCruise,
                contractServiceType.tour,
            ]
            return availableServices.includes(this.contract.serviceType)
        }

        get activeDiscounts() {
            return this.discounts.filter(discount => discount.active)
        }

        get contractServiceType() {
            return contractServiceType
        }

        get isShowMinMaxNights() {
            const availableServices = [contractServiceType.ownHotel, contractServiceType.ownCarRent]
            return availableServices.includes(this.contract.serviceType)
        }
    }

    export default ProductPricesTariffsModal
</script>

<style scoped lang="scss">
    // UI-37: light teal-blue "note" box — same look as the meal-type disclaimer (UI-36).
    .name-visible-disclaimer {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border-radius: 6px;
        background-color: #e7f3f4;
        border-left: 3px solid #51b5bf;
        color: #2d7784;
        font-size: 13px;
        line-height: 1.35;

        .v-icon {
            color: #51b5bf;
            flex-shrink: 0;
            opacity: 1;
        }
    }
</style>
